/** @jsx jsx */
import { useRef, useState, useEffect, useReducer } from 'react';
import { jsx, css } from '@emotion/core';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { Swiper, SwiperSlide } from 'swiper/react';
import useKeyPressEvent from 'react-use/lib/useKeyPressEvent';
import SwiperCore from 'swiper';
import 'swiper/swiper-bundle.css';

import * as S from './DesktopSwipers.styles';
import { Button, BulletedNumber } from '~/styles/index.styles';

import Icon from '~/core/scripts/components/Icon';
import { getMediaQuery } from '~/core/scripts/mediaquery-helper';
// import { clamp } from '~/core/scripts/chart-components/utils';

SwiperCore.use([]);

const DesktopSwiper = () => {
  const smallMultiplesRef = useRef(null);
  const mainSwiperRef = useRef(null);
  const isDrawingEnabled = useStoreState((state) => state.app.isDrawingEnabled);
  const indicators = useStoreState((state) => state.app.indicators);
  const setIndicator = useStoreActions((actions) => actions.app.setIndicator);
  const selectedIndicator = useStoreState(
    (state) => state.app.selectedIndicator
  );
  const selectedIndicatorIndex = indicators
    .map((d) => d.key)
    .indexOf(selectedIndicator.key);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const mediaQuery = getMediaQuery(1200);
  const isWide = mediaQuery.matches;

  useEffect(() => {
    mediaQuery.addListener(forceUpdate);
    return () => {
      mediaQuery.removeListener(forceUpdate);
    };
  }, []);

  const next = () => {
    if (!!mainSwiperRef.current && !isDrawingEnabled) {
      mainSwiperRef.current.swiper.slideNext();
    }
  };

  const previous = () => {
    if (!!mainSwiperRef.current && !isDrawingEnabled) {
      mainSwiperRef.current.swiper.slidePrev();
    }
  };

  useKeyPressEvent('ArrowRight', next);
  useKeyPressEvent('ArrowLeft', previous);

  const slideGroupSize = isWide ? 4 : 3;

  const onMainSwiperChange = ({ activeIndex }) => {
    let indexToGoTo = (activeIndex - 1) % indicators.length;
    // when the first slide is active and the user clicks left, it should go to the last one
    if (indexToGoTo === -1) indexToGoTo = indicators.length - 1;
    if (!Number.isNaN(indexToGoTo) && indexToGoTo < indicators.length) {
      const indicatorToBeSelected = indicators[indexToGoTo];
      if (indicatorToBeSelected) {
        setIndicator(indicatorToBeSelected);
      }
    }
  };

  useEffect(() => {
    if (!!mainSwiperRef.current) {
      mainSwiperRef.current.swiper.slideTo(selectedIndicatorIndex + 1);
    }
    // this is really weird but somehow in the smallMultiples we don't need to add 1 ???
    if (!!smallMultiplesRef.current && selectedIndicatorIndex >= 0) {
      //console.log(selectedIndicatorIndex);
      smallMultiplesRef.current.swiper.slideTo(selectedIndicatorIndex);
    }
  }, [selectedIndicator]);

  return (
    <S.DesktopSwipersOuter isDrawingEnabled={isDrawingEnabled}>
      <S.SmallMultiplesTitle>Kriterien erkunden</S.SmallMultiplesTitle>
      <S.SmallMultiplesWrapper>
        <Swiper
          ref={smallMultiplesRef}
          slidesPerGroup={slideGroupSize}
          threshold={10}
          slidesPerView={slideGroupSize}
          spaceBetween={10}
          navigation={false}
          onSlideChange={forceUpdate}
        >
          {indicators.map((indicator, i) => (
            <SwiperSlide key={indicator.key + i}>
              <Button
                css={S.slide}
                active={selectedIndicator.key === indicator.key}
                onClick={() => setIndicator(indicator)}
              >
                <S.SlideMap src={indicator.staticMapSrc} />
                <S.SlideLabel
                  dangerouslySetInnerHTML={{ __html: indicator.shortLabel }}
                />
              </Button>
            </SwiperSlide>
          ))}
        </Swiper>
        <Button
          circle
          css={S.nextButton}
          disabled={
            !!smallMultiplesRef.current &&
            smallMultiplesRef.current.swiper.isEnd
          }
          onClick={() => {
            if (!!smallMultiplesRef.current) {
              smallMultiplesRef.current.swiper.slideNext();
            }
          }}
        >
          <Icon type="right" />
        </Button>
        <Button
          circle
          css={S.prevButton}
          disabled={
            !!smallMultiplesRef.current &&
            smallMultiplesRef.current.swiper.isBeginning
          }
          onClick={() => {
            if (!!smallMultiplesRef.current) {
              smallMultiplesRef.current.swiper.slidePrev();
            }
          }}
        >
          <Icon type="left" style={{ transform: 'translateX(-2px)' }} />
        </Button>
      </S.SmallMultiplesWrapper>
      <S.MainSwiperWrapper>
        <Swiper
          ref={mainSwiperRef}
          loop
          slidesPerView={1}
          spaceBetween={0}
          navigation={false}
          onSlideChange={onMainSwiperChange}
        >
          {indicators.map((indicator, i) => (
            <SwiperSlide key={indicator.key + i}>
              <S.IndicatorTitle>
                <BulletedNumber big inline>
                  <span>{i + 1}</span>
                </BulletedNumber>
                <span
                  dangerouslySetInnerHTML={{ __html: indicator.shortLabel }}
                />
              </S.IndicatorTitle>
              <S.IndicatorLabel
                dangerouslySetInnerHTML={{ __html: indicator.label }}
              />
              <S.IndicatorText
                dangerouslySetInnerHTML={{
                  __html: indicator.description || '',
                }}
              />
              <S.IndicatorSource
                css={css`
                  text-align: left;
                `}
                dangerouslySetInnerHTML={{
                  __html: indicator.source || '',
                }}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <S.PlainPrevButton onClick={previous}>
          <Icon type="tall_caret_left" />
        </S.PlainPrevButton>
        <S.PlainNextButton onClick={next}>
          <Icon type="tall_caret_right" />
        </S.PlainNextButton>
      </S.MainSwiperWrapper>
    </S.DesktopSwipersOuter>
  );
};

export default DesktopSwiper;
